/**
 * One group's standings table (Scores UX spec v1.0 section 10). Every column
 * and every header comes from the server response passed in as props: this
 * file must never name a sport or hardcode a column key.
 *
 * Anatomy: a 32px header row on `surfaceSunken` with `small` 600
 * `textTertiary` labels, then 44px rows with a hairline between them and no
 * zebra striping. Two columns are frozen while the stat columns scroll
 * sideways inside the card:
 *   - rank, 28 wide, `small` `textTertiary` tabular, the server's own label;
 *   - team, a 28px mark (the score row's, so a three letter plate reads; the
 *     spec's 20 holds only two letters), 8, then the longest server name that fits the cell
 *     (`pickTeamLabel`, never an ellipsis); at least 140 wide at phone width
 *     and as wide as the full name from 640px.
 * Stat columns are 48 wide, right aligned `body` tabular.
 *
 * A followed team's row carries a 16px `primary` star after the name and a
 * 4px `primary` edge on its left; weight is never the cue. The row is one
 * link into that team's page. There is no follow control in a cell: following
 * lives on the team page. Followed teams come from `profile.favoriteTeamIds`,
 * the one account wide read the follow writes invalidate.
 */
import { tokens } from "@sportsmet/theme";
import { pickTeamLabel } from "@sportsmet/theme/scores";
import { useProfileGet } from "@sportsmet/profile-sdk/private";
import { cn, FOCUS_RING } from "@sportsmet/ui";
import { Link } from "@tanstack/react-router";
import { type ReactNode, useRef } from "react";

import { FavoriteStarIcon } from "../../icons/glyphs";
import { useIsScrollEndClipped } from "../gameDetail/scrollEndCue";
import { TeamDisc } from "../scores/TeamDisc";
import { useMeasuredWidth } from "../scores/useMeasuredWidth";
import { cellText, rowAccessibilityLabel } from "./standingsTableLogic";
import {
  STANDINGS_TEAM_HEADER,
  type typeStandingsColumn,
  type typeStandingsTableRow,
} from "./standingsTypes";

/** The team cell's floor at phone width (spec 10). */
const TEAM_CELL_MIN_PX = 140;
const NAME_FONT_PX = tokens.text.body.size;
/** The 28 mark and its 8 gap, before the name. */
const MARK_AND_GAP_PX = 36;
/** The star and its 4 gap, after a followed team's name. */
const STAR_AND_GAP_PX = 20;

const RANK_CELL_CLASS =
  "sticky left-0 z-10 w-7 min-w-7 pl-3 text-left text-small text-ink-tertiary tabular-nums";
const TEAM_CELL_CLASS =
  "sticky left-7 z-10 min-w-35 pr-2 pl-1 text-left sm:min-w-0 sm:whitespace-nowrap";
// whitespace-nowrap: a record ("13-0") or a tie rank ("T-20") never breaks
// across two lines (ISSUE-1388 QA round 3); the cell grows past w-12.
const STAT_CELL_CLASS =
  "w-12 min-w-12 whitespace-nowrap px-2 text-right tabular-nums";

function TeamCell(props: {
  row: typeStandingsTableRow;
  isFollowed: boolean;
}): ReactNode {
  const { row } = props;
  const { ref, widthPx } = useMeasuredWidth<HTMLSpanElement>(
    TEAM_CELL_MIN_PX - MARK_AND_GAP_PX,
  );
  const label = pickTeamLabel(
    {
      name: row.teamName,
      shortName: row.shortName,
      abbreviation: row.teamAbbr,
    },
    widthPx - (props.isFollowed ? STAR_AND_GAP_PX : 0),
    NAME_FONT_PX,
  );
  return (
    <span className="flex min-w-0 items-center gap-2">
      <TeamDisc
        plateText={row.teamAbbr}
        logoUrl={row.logoUrl}
        size="row"
        testId="team-mark"
        imageTestId="team-mark-image"
      />
      <span ref={ref} className="flex min-w-0 flex-1 items-center gap-1">
        <span
          className="min-w-0 overflow-hidden whitespace-nowrap text-body text-ink"
          data-testid="standings-team-name"
          data-full-name={row.teamName}
        >
          {label}
        </span>
        {props.isFollowed ? (
          <FavoriteStarIcon
            className="text-primary"
            testId="standings-followed-star"
          />
        ) : null}
      </span>
    </span>
  );
}

export const StandingsTable = ({
  caption,
  primaryColumns,
  secondaryColumns,
  rows,
}: {
  /** Names the table for a screen reader. The group's own label, e.g. "AFC East". */
  caption: string;
  primaryColumns: typeStandingsColumn[];
  secondaryColumns: typeStandingsColumn[];
  rows: typeStandingsTableRow[];
}): ReactNode => {
  const columns = [...primaryColumns, ...secondaryColumns];
  const profile = useProfileGet({});
  const followedTeamIds = profile.data?.data?.profile?.favoriteTeamIds ?? [];

  const scrollRef = useRef<HTMLDivElement>(null);
  // SMOKE-4: columns past the card's right edge get a fade until the fan
  // scrolls to the end, so a cut column never reads as the last one.
  const isEndClipped = useIsScrollEndClipped(scrollRef);

  return (
    <div className="relative">
      <div
        ref={scrollRef}
        className="overflow-x-auto"
        data-testid="standings-table-scroll"
      >
        <table className="w-full border-separate border-spacing-0 text-body">
          <caption className="sr-only">{caption}</caption>
          <thead>
            <tr data-testid="standings-header-row">
              <th
                scope="col"
                className={cn(
                  RANK_CELL_CLASS,
                  "h-8 bg-surface-sunken font-semibold",
                )}
              >
                <span className="sr-only">Rank</span>
              </th>
              <th
                scope="col"
                className={cn(
                  TEAM_CELL_CLASS,
                  "h-8 bg-surface-sunken text-small font-semibold text-ink-tertiary",
                )}
              >
                {STANDINGS_TEAM_HEADER}
              </th>
              {columns.map((column) => (
                <th
                  key={column.key}
                  scope="col"
                  className={cn(
                    STAT_CELL_CLASS,
                    "h-8 bg-surface-sunken text-small font-semibold text-ink-tertiary",
                  )}
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => {
              const isFollowed = followedTeamIds.includes(row.id);
              const rule =
                index === rows.length - 1
                  ? undefined
                  : "border-b border-hairline";
              return (
                <tr
                  key={row.id}
                  className="relative"
                  data-testid="standings-row"
                  data-followed={isFollowed ? "true" : undefined}
                >
                  <td
                    className={cn(
                      RANK_CELL_CLASS,
                      rule,
                      "h-touch-target bg-surface",
                      // The followed row's 4px primary edge.
                      isFollowed
                        ? "before:absolute before:inset-y-0 before:left-0 before:w-1 before:bg-primary before:content-['']"
                        : undefined,
                    )}
                    data-testid="standings-rank"
                  >
                    {row.rankLabel}
                  </td>
                  <th
                    scope="row"
                    className={cn(
                      TEAM_CELL_CLASS,
                      rule,
                      "h-touch-target bg-surface font-normal",
                    )}
                  >
                    <Link
                      to="/scores"
                      // `from` keeps `prev` narrowed to the /scores search shape.
                      from="/scores"
                      search={(prev) => ({ ...prev, teamId: row.id })}
                      aria-label={rowAccessibilityLabel({
                        rankLabel: row.rankLabel,
                        teamName: row.teamName,
                        columns,
                        values: row.values,
                      })}
                      className={`flex min-h-touch-target items-center text-ink no-underline after:absolute after:inset-0 after:content-[''] ${FOCUS_RING}`}
                    >
                      <TeamCell row={row} isFollowed={isFollowed} />
                    </Link>
                  </th>
                  {columns.map((column) => (
                    <td
                      key={column.key}
                      className={cn(
                        STAT_CELL_CLASS,
                        rule,
                        "h-touch-target bg-surface",
                      )}
                    >
                      {cellText(row.values, column.key)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {isEndClipped ? (
        <div
          aria-hidden="true"
          data-testid="standings-scroll-cue-end"
          className="pointer-events-none absolute inset-y-0 right-0 z-20 w-8 bg-linear-to-l from-surface to-transparent"
        />
      ) : null}
    </div>
  );
};
